import { useState } from "react";
import { FileMinus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatError } from "@/lib/api";

const ACTIONS = [["keep", "Keep access"], ["remove_subjects", "Remove some subjects"], ["cut", "Stop access"]];

export default function CreditNoteButton({ sub, onDone }) {
  const [open, setOpen] = useState(false);
  const left = sub.total - (sub.credited || 0);
  const [amount, setAmount] = useState(left);
  const [reason, setReason] = useState("");
  const [effects, setEffects] = useState({});
  const [busy, setBusy] = useState(false);
  if (left <= 0) return <span className="text-xs text-rose-300" data-testid={`credit-full-${sub.id}`}>Fully credited</span>;

  const eff = (cid) => effects[cid] || { action: "keep", remove_subjects: [] };
  const setEff = (cid, v) => setEffects((e) => ({ ...e, [cid]: { ...eff(cid), ...v } }));
  const toggleSubj = (cid, s) => {
    const cur = eff(cid).remove_subjects;
    setEff(cid, { remove_subjects: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/billing/${sub.id}/credit-note`, {
        amount: Number(amount), reason, effects: sub.items.map((it) => ({ child_id: it.child_id, ...eff(it.child_id) })) });
      toast.success(`Credit note ${data.credit_note.cn_no} created and emailed to the parent (copy to you).`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => { setAmount(left); setReason(""); setEffects({}); setOpen(true); }} data-testid={`credit-note-btn-${sub.id}`}
        className="inline-flex items-center gap-1 rounded-full border-2 border-rose-400 text-rose-200 px-3 py-1 text-xs font-semibold hover:bg-rose-500/20 whitespace-nowrap">
        <FileMinus size={12} /> Create credit note
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid={`credit-note-dialog-${sub.id}`}>
          <DialogHeader><DialogTitle>Credit note for {sub.invoice_no || "this payment"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-500">Paid Rs {sub.total}{sub.credited ? `, already credited Rs ${sub.credited}` : ""} · up to Rs {left} can be credited.</p>
            <label className="block font-semibold">Amount (Rs)
              <input type="number" min={1} max={left} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="credit-amount"
                className="mt-1 w-full rounded-xl border-2 border-slate-300 px-3 py-2" />
            </label>
            <label className="block font-semibold">Reason
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="e.g. Parent removed 2 subjects" data-testid="credit-reason"
                className="mt-1 w-full rounded-xl border-2 border-slate-300 px-3 py-2" />
            </label>
            {sub.items.map((it) => (
              <div key={it.child_id} className="rounded-xl border-2 border-slate-200 p-2.5" data-testid={`credit-child-${it.child_id}`}>
                <p className="font-semibold">{it.child_name} (Grade {it.grade})</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {ACTIONS.map(([a, label]) => (
                    <button key={a} type="button" onClick={() => setEff(it.child_id, { action: a })} data-testid={`credit-action-${it.child_id}-${a}`}
                      className={`px-2.5 py-1 rounded-full border-2 text-xs font-semibold ${eff(it.child_id).action === a ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>{label}</button>
                  ))}
                </div>
                {eff(it.child_id).action === "remove_subjects" && (
                  <div className="flex flex-wrap gap-3 mt-2">
                    {it.subjects.map((s) => (
                      <label key={s} className="flex items-center gap-1.5 text-xs">
                        <input type="checkbox" checked={eff(it.child_id).remove_subjects.includes(s)} onChange={() => toggleSubj(it.child_id, s)} /> {s}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button onClick={create} disabled={busy || reason.trim().length < 3 || !(Number(amount) > 0)} data-testid="credit-create"
              className="w-full rounded-full bg-rose-600 text-white font-bold py-2.5 inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <FileMinus size={15} />} Create credit note (PDF + email)
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
